/**
 * Pure logic behind the email sender registry editor.
 *
 * Kept out of the component so the rules can be tested directly — they mirror
 * `_coerce_email_sending` in sm4rt-api, which is the authority. The point of
 * duplicating them here is immediate feedback: the backend rejects the whole
 * save, so an admin who only finds out on Save has to work out which of
 * several rows was wrong.
 *
 * Two rules do real work:
 *
 *  - **Verified domains.** smtp2go refuses to send from anything else, so an
 *    address off them isn't a cosmetic problem — every message on that sender
 *    fails until somebody notices.
 *  - **Referential integrity.** A binding naming a sender that no longer
 *    exists resolves to the fallback and sends quite happily from the wrong
 *    address. Renaming a sender therefore has to carry its bindings with it.
 */

import type {
  EmailSenderConfig,
  EmailSendingConfig,
  EmailMessageKeyInfo,
} from '@techgitter/contracts';
import {
  EMAIL_BINDING_DEFAULTS,
  EMAIL_FALLBACK_SENDER,
  EMAIL_SENDER_DEFAULTS,
  VERIFIED_SENDING_DOMAINS,
} from '@techgitter/contracts';

/** Same loose shape the backend uses — a paste-accident check, not RFC 5322. */
const EMAIL_RE = /^[^@\s,;<>]+@[^@\s,;<>]+\.[^@\s,;<>]+$/;

export interface SenderRow extends EmailSenderConfig {
  /** The map key. Editable, which is why renames have to remap bindings. */
  name: string;
}

/** A fresh copy of the built-in registry, for seeding the editor. */
export function seedEmailSending(): EmailSendingConfig {
  const senders: Record<string, EmailSenderConfig> = {};
  Object.entries(EMAIL_SENDER_DEFAULTS).forEach(([name, sender]) => {
    senders[name] = { ...sender };
  });
  return {
    senders,
    bindings: { ...EMAIL_BINDING_DEFAULTS },
    fallbackSender: EMAIL_FALLBACK_SENDER,
  };
}

/** The senders as an ordered, editable list. */
export function toSenderRows(config: EmailSendingConfig): SenderRow[] {
  return Object.entries(config.senders).map(([name, sender]) => ({
    name,
    ...sender,
  }));
}

/** Rebuild the config from an edited list, preserving row order. */
export function fromSenderRows(
  rows: SenderRow[],
  bindings: Record<string, string>,
  fallbackSender: string,
): EmailSendingConfig {
  const senders: Record<string, EmailSenderConfig> = {};
  rows.forEach(({ name, ...sender }) => {
    senders[name] = sender;
  });
  return { senders, bindings, fallbackSender };
}

/**
 * Rename a sender, carrying every binding that pointed at it.
 *
 * Without the remap a rename leaves dangling bindings, the backend rejects the
 * whole save, and the error names a sender the admin has just stopped being
 * able to see. The fallback moves too, for the same reason.
 */
export function renameSender(
  config: EmailSendingConfig,
  from: string,
  to: string,
): EmailSendingConfig {
  if (from === to) return config;

  const senders: Record<string, EmailSenderConfig> = {};
  Object.entries(config.senders).forEach(([name, sender]) => {
    senders[name === from ? to : name] = sender;
  });

  const bindings: Record<string, string> = {};
  Object.entries(config.bindings).forEach(([key, name]) => {
    bindings[key] = name === from ? to : name;
  });

  return {
    senders,
    bindings,
    fallbackSender:
      config.fallbackSender === from ? to : config.fallbackSender,
  };
}

/**
 * Remove a sender, and any binding that pointed at it.
 *
 * Dropping the bindings is the honest move: leaving them would make the save
 * fail, and silently repointing them at another sender would send a customer's
 * invoice from somewhere nobody chose.
 */
export function removeSender(
  config: EmailSendingConfig,
  name: string,
): EmailSendingConfig {
  const senders = { ...config.senders };
  delete senders[name];

  const bindings: Record<string, string> = {};
  Object.entries(config.bindings).forEach(([key, boundTo]) => {
    if (boundTo !== name) bindings[key] = boundTo;
  });

  const remaining = Object.keys(senders);
  return {
    senders,
    bindings,
    fallbackSender: remaining.includes(config.fallbackSender)
      ? config.fallbackSender
      : remaining[0] ?? '',
  };
}

/** Whether an address sits on a domain smtp2go will actually send from. */
export function isVerifiedSendingDomain(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return (VERIFIED_SENDING_DOMAINS as readonly string[]).includes(domain);
}

export interface SenderRowErrors {
  name?: string;
  fromEmail?: string;
  bcc?: string;
}

/** Per-row problems, keyed by row index so the inputs can show them inline. */
export function validateSenderRows(
  rows: SenderRow[],
): Record<number, SenderRowErrors> {
  const errors: Record<number, SenderRowErrors> = {};
  const seen = new Map<string, number>();

  rows.forEach((row, idx) => {
    const rowErrors: SenderRowErrors = {};
    const name = row.name.trim();

    if (!name) {
      rowErrors.name = 'Required';
    } else if (seen.has(name)) {
      rowErrors.name = 'Duplicate';
      const first = seen.get(name) as number;
      errors[first] = { ...errors[first], name: 'Duplicate' };
    } else {
      seen.set(name, idx);
    }

    const from = (row.fromEmail ?? '').trim();
    if (!from) {
      rowErrors.fromEmail = 'Required';
    } else if (!EMAIL_RE.test(from)) {
      rowErrors.fromEmail = 'Not an email address';
    } else if (!isVerifiedSendingDomain(from)) {
      rowErrors.fromEmail = 'Domain not verified for sending';
    }

    const bcc = (row.bcc ?? '').trim();
    if (bcc) {
      if (!EMAIL_RE.test(bcc)) {
        rowErrors.bcc = 'Not an email address';
      } else if (!isVerifiedSendingDomain(bcc)) {
        rowErrors.bcc = 'Domain not verified for sending';
      }
    }

    if (Object.keys(rowErrors).length) {
      errors[idx] = { ...errors[idx], ...rowErrors };
    }
  });

  return errors;
}

/** A one-line summary of what a sender actually puts on the wire. */
export function describeSender(sender: EmailSenderConfig | undefined): string {
  if (!sender) return 'Not configured';
  const from = sender.fromName
    ? `${sender.fromName} <${sender.fromEmail}>`
    : sender.fromEmail;
  return sender.bcc ? `${from} · BCC ${sender.bcc}` : from;
}

export interface BindingRow {
  key: string;
  label: string;
  description: string;
  inert: boolean;
  /** Bound sender name, or '' when nothing is bound (falls back). */
  senderName: string;
  resolved: EmailSenderConfig | undefined;
  /** Set when a message whose replies matter goes out from an unmonitored
   *  address — the quiet mistake this editor exists to surface. */
  warning?: string;
  /** True for a binding in storage that no code path is known to send. */
  unknown?: boolean;
}

/**
 * Join the known message keys to the stored bindings.
 *
 * Any stored binding whose key isn't in the catalog is listed too, flagged
 * `unknown`: it either predates the catalog or was hand-written, and hiding it
 * would leave an invisible rule in effect.
 */
export function toBindingRows(
  config: EmailSendingConfig,
  catalog: readonly EmailMessageKeyInfo[],
): BindingRow[] {
  const rows: BindingRow[] = catalog.map((info) => {
    const senderName = config.bindings[info.key] ?? '';
    const effectiveName = senderName || config.fallbackSender;
    const resolved = config.senders[effectiveName];
    const row: BindingRow = {
      key: info.key,
      label: info.label,
      description: info.description,
      inert: Boolean(info.inert),
      senderName,
      resolved,
    };
    if (info.expectsReplies && resolved && !resolved.monitored) {
      row.warning = 'Replies to this address are not monitored';
    }
    return row;
  });

  const known = new Set(catalog.map((i) => i.key));
  Object.entries(config.bindings)
    .filter(([key]) => !known.has(key))
    .forEach(([key, senderName]) => {
      rows.push({
        key,
        label: key,
        description: 'Not a message key this app is known to send.',
        inert: false,
        senderName,
        resolved: config.senders[senderName],
        unknown: true,
      });
    });

  return rows;
}
